import toPairs from 'lodash/toPairs';
import { types as sdkTypes } from './sdkLoader';
import { diffInTime } from './dates';
import { extractYouTubeID } from './string';
import { isValidPhoneNumber, isPossiblePhoneNumber } from 'react-phone-number-input';

const { LatLng, Money } = sdkTypes;

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 256;

const isNonEmptyString = val => {
  return typeof val === 'string' && val.trim().length > 0;
};

/**
 * Validator functions and helpers for Final Forms
 */

// Final Form expects and undefined value for a successful validation
const VALID = undefined;

export const required = message => value => {
  if (typeof value === 'undefined' || value === null) {
    // undefined or null values are invalid
    return message;
  }
  if (typeof value === 'string') {
    // string must be nonempty when trimmed
    return isNonEmptyString(value) ? VALID : message;
  }
  return VALID;
};

export const requiredStringNoTrim = message => value => {
  return typeof value === 'string' && value.length > 0 ? VALID : message;
};

// DEPRECATED in favor of required
export const requiredBoolean = message => value => {
  return typeof value === 'boolean' ? VALID : message;
};

// DEPRECATED in favor of required
export const requiredAndNonEmptyString = message => value => {
  return isNonEmptyString(value) ? VALID : message;
};

/**
 * Validates that a string is unique in an array of strings.
 * @param {Array<string>} stringArray - Array of strings to check against.
 * @param {Function} getMessage - Function that returns an error message.
 * @param {Function} toSlug - Function that converts a string to a slug.
 * @returns {string} - VALID if the string is unique, otherwise the error message.
 */
export const uniqueString = (currentIndex, stringArray, getMessage, toSlug) => value => {
  if (typeof value === 'undefined' || value === null) {
    // undefined or null values are invalid
    return getMessage('', '');
  }
  const slug = toSlug(value);
  const otherSlugs = stringArray.map(toSlug).filter((_, i) => i !== currentIndex);
  const isUnique = !otherSlugs.includes(slug);
  return isUnique ? VALID : getMessage(value, slug);
};

export const requiredFieldArrayCheckbox = message => value => {
  if (!value) {
    return message;
  }

  const entries = toPairs(value);
  const hasSelectedValues = entries.filter(e => !!e[1]).length > 0;
  return hasSelectedValues ? VALID : message;
};

export const requiredSelectTreeOption = message => value => {
  if (typeof value === 'undefined' || value === null || Object.values(value)?.length === 0) {
    return message;
  }
};

export const minLength = (message, minimumLength) => value => {
  const hasLength = value && typeof value.length === 'number';
  return hasLength && value.length >= minimumLength ? VALID : message;
};

export const maxLength = (message, maximumLength) => value => {
  if (!value) {
    return VALID;
  }
  const hasLength = value && typeof value.length === 'number';
  return hasLength && value.length <= maximumLength ? VALID : message;
};

export const passwordStrong = (message) => (value) => {
  if (!value) return null; 
  const strongPasswordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/;
  return strongPasswordRegex.test(value) ? null : message;
};

export const nonEmptyArray = message => value => {
  return value && Array.isArray(value) && value.length > 0 ? VALID : message;
};

export const autocompleteSearchRequired = message => value => {
  return value && value.search ? VALID : message;
};

export const autocompletePlaceSelected = message => value => {
  const selectedPlaceIsValid =
    value &&
    value.selectedPlace &&
    value.selectedPlace.address &&
    value.selectedPlace.origin instanceof LatLng;
  return selectedPlaceIsValid ? VALID : message;
};

export const bookingDateRequired = inValidDateMessage => value => {
  const dateIsValid = value && value.date instanceof Date;
  return !dateIsValid ? inValidDateMessage : VALID;
};

export const bookingDatesRequired = (inValidStartDateMessage, inValidEndDateMessage) => value => {
  const startDateIsValid = value && value.startDate instanceof Date;
  const endDateIsValid = value && value.endDate instanceof Date;

  if (!startDateIsValid) {
    return inValidStartDateMessage;
  } else if (!endDateIsValid) {
    return inValidEndDateMessage;
  } else {
    return VALID;
  }
};

// Source: http://www.regular-expressions.info/email.html
// See the link above for an explanation of the tradeoffs.
const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export const emailFormatValid = message => value => {
  return value && EMAIL_RE.test(value) ? VALID : message;
};

export const multipleEmailsFormatValid = message => value => {
  if (!value || value.trim() === '') {
    return VALID; // Optional field - empty is valid
  }
  const emails = value.split(',').map(email => email.trim()).filter(email => email !== '');
  if (emails.length === 0) {
    return VALID;
  }
  const allValid = emails.every(email => EMAIL_RE.test(email));
  return allValid ? VALID : message;
};

export const moneySubUnitAmountAtLeast = (message, minValue) => value => {
  return value instanceof Money && value.amount >= minValue ? VALID : message;
};

const parseNum = str => {
  const num = Number.parseInt(str, 10);
  return Number.isNaN(num) ? null : num;
};

export const numberAtLeast = (message, minNumber) => value => {
  const valueNum = parseNum(value);
  return typeof valueNum === 'number' && valueNum >= minNumber ? VALID : message;
};

export const validateInteger = (value, max, min, numberTooSmallMessage, numberTooBigMessage) => {
  const parsedValue = Number.parseInt(value, 10);
  if (parsedValue > max) {
    return numberTooBigMessage;
  }
  if (parsedValue < min) {
    return numberTooSmallMessage;
  }
  return VALID;
};

// If URL is passed to this function as null, will return VALID
export const validateYoutubeURL = (url, message) => {
  return url ? (extractYouTubeID(url) ? VALID : message) : VALID;
};

export const ageAtLeast = (message, minYears) => value => {
  const { year, month, day } = value;
  const dayNum = parseNum(day);
  const monthNum = parseNum(month);
  const yearNum = parseNum(year);

  // day, month, and year needs to be numbers
  if (dayNum !== null && monthNum !== null && yearNum !== null) {
    const now = new Date();
    const age = new Date(yearNum, monthNum - 1, dayNum);
    const ageInYears = diffInTime(now, age, 'years', true);

    return age && age instanceof Date && ageInYears >= minYears ? VALID : message;
  }
  return message;
};

export const validBusinessURL = message => value => {
  if (typeof value === 'undefined' || value === null) {
    return message;
  }

  const disallowedChars = /[^-A-Za-z0-9+&@#/%?=~_|!:,.;()]/;
  const protocolTokens = value.split(':');
  const includesProtocol = protocolTokens.length > 1;
  const usesHttpProtocol = includesProtocol && !!protocolTokens[0].match(/^(https?)/);

  const invalidCharacters = !!value.match(disallowedChars);
  const invalidProtocol = !(usesHttpProtocol || !includesProtocol);
  // Stripe checks against example.com
  const isExampleDotCom = !!value.match(/^(https?:\/\/example\.com|example\.com)/);
  const isLocalhost = !!value.match(/^(https?:\/\/localhost($|:|\/)|localhost($|:|\/))/);
  return invalidCharacters || invalidProtocol || isExampleDotCom || isLocalhost ? message : VALID;
};

export const validSsnLast4 = message => value => {
  return value.length === 4 ? VALID : message;
};

export const validHKID = message => value => {
  // Accept value 000000000 for testing Stripe
  if (value.length === 9 && value.match(/([0]{9})/)) {
    return VALID;
  }

  // HKID format example: AB364912(5)
  // ID can start with one or two letters and the check digit in the end can be in brackets or not
  if (value.length < 8) {
    return message;
  }

  // Handle possible brackets in value
  if (value.charAt(value.length - 3) === '(' && value.charAt(value.length - 1) === ')') {
    value = value.substring(0, value.length - 3) + value.charAt(value.length - 2);
  }
  value = value.toUpperCase();

  // Check that pattern is correct and split value to array
  const hkidPattern = /^([A-Z]{1,2})([0-9]{6})([A0-9])$/;
  const matchArray = value.match(hkidPattern);

  if (!matchArray) {
    return message;
  }

  const charPart = matchArray[1];
  const numPart = matchArray[2];
  const checkDigit = matchArray[3];

  // Calculate the checksum for character part.
  // Transfer letters to numbers so that A=10, B=11, C=12 etc.
  // If there is only one letter in the ID use 36 as the first value
  // Total calculation is weighted so that 1st digit is x9, 2nd digit x8, 3rd digit x7 etc.

  const strValidChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let checkSum = 0;

  if (charPart.length === 2) {
    checkSum += 9 * (10 + strValidChars.indexOf(charPart.charAt(0)));
    checkSum += 8 * (10 + strValidChars.indexOf(charPart.charAt(1)));
  } else {
    checkSum += 9 * 36;
    checkSum += 8 * (10 + strValidChars.indexOf(charPart));
  }

  // Calculate the checksum for numeric part

  for (let i = 0, j = 7; i < numPart.length; i++, j--) {
    checkSum += j * numPart.charAt(i);
  }

  // Verify the check digit
  const remaining = checkSum % 11;
  let verify = remaining === 0 ? 0 : 11 - remaining;
  verify = verify.toString();
  const isValid = verify === checkDigit || (verify === 10 && checkDigit === 'A');

  return isValid ? VALID : message;
};

export const validSGID = message => value => {
  return value.length === 9 ? VALID : message;
};

export const composeValidators = (...validators) => value =>
  validators.reduce((error, validator) => error || validator(value), VALID);

export const validatePhoneNumber = message => value => {
  if (!value) return;
  const validPhoneNumberCheck = isValidPhoneNumber(value);
  const isPossiblePhoneNumberCheck = isPossiblePhoneNumber(value);
  const condition = validPhoneNumberCheck && isPossiblePhoneNumberCheck;
  return condition ? VALID : message;
};

export const validateAddNewOption = (value, allValues, fieldName) => {
  // For single select fields
  if (typeof value === 'string' && value.includes('-- Add New --')) {
    return 'Please enter a new option or select an existing one';
  }
  
  // For multi-select fields (array of strings)
  if (Array.isArray(value) && value.some(v => v?.includes?.('-- Add New --'))) {
    return 'Please enter a new option or remove "-- Add New --" selection';
  }
  
  return undefined;
};

// For FieldArray with objects
export const validateAddNewOptionInArray = (value, allValues, fieldName) => {
  if (!value || !Array.isArray(value)) return undefined;
  
  const hasAddNewWithoutInput = value.some(item => 
    item?.name?.includes?.('-- Add New --')
  );
  
  if (hasAddNewWithoutInput) {
    return 'Please enter new options or select existing ones for all skills';
  }
  
  return undefined;
};

// Generic validator that works for all cases
export const validateNoAddNewPending = (value) => {
  // First, check for the specific invalid "-- Add New --" state.
  // This check must happen before the generic 'required' check.

  // Case 1: Single select field (value is a string).
  if (typeof value === 'string' && value.includes('-- Add New --')) {
    return 'If you select "--Add new--", you must add a new value in its place.';
  }

  // Case 2: Multi-select or FieldArray (value is an array).
  if (Array.isArray(value)) {
    // Subcase 2a: Array of strings (from a multi-select component).
    if (value.some(v => typeof v === 'string' && v.includes('-- Add New --'))) {
      return 'Please enter a new option or remove "-- Add New --" selection';
    }

    // Subcase 2b: Array of objects (from a FieldArray component).
    if (value.some(item => item?.name?.includes?.('-- Add New --'))) {
      return 'Please enter new options or select existing ones for all items';
    }
  }

  // Then, apply the generic 'required' validation from the original function.
  if (typeof value === 'undefined' || value === null) {
    // undefined or null values are invalid
    return 'This field is required';
  }
  if (typeof value === 'string') {
    // string must be nonempty when trimmed
    return isNonEmptyString(value) ? VALID : 'This field is required';
  }

  return undefined;
};

// validator that works for only no add new case cases
export const validateOnlyNoAddNewPending = (value) => {
  // First, check for the specific invalid "-- Add New --" state.
  // This check must happen before the generic 'required' check.

  // Case 1: Single select field (value is a string).
  if (typeof value === 'string' && value.includes('-- Add New --')) {
    return 'If you select "--Add new--", you must add a new value in its place.';
  }

  // Case 2: Multi-select or FieldArray (value is an array).
  if (Array.isArray(value)) {
    // Subcase 2a: Array of strings (from a multi-select component).
    if (value.some(v => typeof v === 'string' && v.includes('-- Add New --'))) {
      return 'Please enter a new option or remove "-- Add New --" selection';
    }

    // Subcase 2b: Array of objects (from a FieldArray component).
    if (value.some(item => item?.name?.includes?.('-- Add New --'))) {
      return 'Please enter new options or select existing ones for all items';
    }
  }

  return undefined;
};

export const validateWebUrl = value => {
  if (!value) {
    return VALID;
  }

  const trimmedValue = value.trim();
  
  if (/\s/.test(trimmedValue)) {
    return 'Please enter a valid URL';
  }

  let url;
  try {
    const urlToParse = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(trimmedValue) 
      ? trimmedValue 
      : `http://${trimmedValue}`;
    url = new URL(urlToParse);
  } catch (e) {
    return 'Please enter a valid URL';
  }

  if (!['http:', 'https:', 'ftp:'].includes(url.protocol)) {
    return 'Please enter a valid URL';
  }

  const hostname = url.hostname.toLowerCase();

  if (hostname === 'localhost') {
    return VALID;
  }

  // Check for IP address (v4)
  const ipv4Pattern = /^(\d{1,3}\.){3}\d{1,3}$/;
  if (ipv4Pattern.test(hostname)) {
    const octets = hostname.split('.');
    const validIP = octets.every(octet => {
      const num = parseInt(octet, 10);
      return num >= 0 && num <= 255 && octet === num.toString();
    });
    if (validIP) {
      return VALID;
    }
    return 'Please enter a valid URL';
  }

  if (hostname.includes(':')) {
    return VALID;
  }

  if (!hostname.includes('.')) {
    return 'Please enter a valid URL';
  }

  const labels = hostname.split('.');
  
  if (labels.length < 2) {
    return 'Please enter a valid URL';
  }

  const labelPattern = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
  for (const label of labels) {
    if (!label || label.length > 63) {
      return 'Please enter a valid URL';
    }
    if (!labelPattern.test(label)) {
      return 'Please enter a valid URL';
    }
  }

  const tld = labels[labels.length - 1];
  
  if (tld.length < 2 || !/^[a-z]+$/.test(tld)) {
    return 'Please enter a valid URL';
  }

  // Validate against known TLDs
  // This list includes common gTLDs, ccTLDs, and new gTLDs
  const validTLDs = new Set([
    // Generic TLDs
    'com', 'org', 'net', 'edu', 'gov', 'mil', 'int',
    // Common country codes
    'us', 'uk', 'ca', 'au', 'de', 'fr', 'it', 'es', 'nl', 'be', 'ch', 'at',
    'se', 'no', 'dk', 'fi', 'pl', 'cz', 'ru', 'cn', 'jp', 'kr', 'in', 'br',
    'mx', 'ar', 'cl', 'co', 'za', 'nz', 'ie', 'pt', 'gr', 'tr', 'il', 'ae',
    'sa', 'sg', 'my', 'th', 'vn', 'id', 'ph', 'pk', 'bd', 'eg', 'ng', 'ke',
    // New gTLDs (popular ones)
    'app', 'dev', 'io', 'ai', 'tech', 'online', 'site', 'website', 'store',
    'shop', 'blog', 'news', 'media', 'info', 'biz', 'name', 'pro', 'mobi',
    'tel', 'travel', 'jobs', 'xyz', 'top', 'club', 'live', 'today', 'world',
    'life', 'co', 'me', 'tv', 'cc', 'ws', 'la', 'ac', 'ag', 'am', 'as', 'bz',
    // Industry specific
    'museum', 'aero', 'coop', 'asia', 'cat', 'jobs', 'post', 'tel', 'xxx',
    // Tech/business
    'solutions', 'services', 'partners', 'enterprises', 'ventures', 'company',
    'center', 'management', 'consulting', 'finance', 'financial', 'capital',
    'investments', 'holdings', 'group', 'corporation', 'industries',
    // Creative
    'photography', 'photos', 'pics', 'pictures', 'graphics', 'design',
    'art', 'gallery', 'studio', 'agency', 'digital', 'media', 'video',
    // Other common
    'academy', 'education', 'school', 'university', 'college', 'institute',
    'training', 'courses', 'guru', 'coach', 'expert', 'ninja', 'rocks',
    'zone', 'space', 'place', 'land', 'city', 'town', 'country', 'global'
  ]);

  if (!validTLDs.has(tld)) {
    return 'Please enter a valid URL';
  }

  if (hostname.length > 253) {
    return 'Please enter a valid URL';
  }

  return VALID;
};

export const validateLinkedinUrl = value => {
  if (!value) {
    return VALID;
  }

  const trimmedValue = value.trim();

  // LinkedIn URL patterns to support:
  // 1. Personal profiles: linkedin.com/in/username
  // 2. Company pages: linkedin.com/company/company-name
  // 3. School pages: linkedin.com/school/school-name
  // 4. Showcase pages: linkedin.com/showcase/page-name
  // 5. Posts/articles: linkedin.com/posts/*, linkedin.com/pulse/*
  // 6. Jobs: linkedin.com/jobs/*
  // 7. Groups: linkedin.com/groups/*
  // 8. Events: linkedin.com/events/*
  // 9. Learning: linkedin.com/learning/*
  // 10. Feed items: linkedin.com/feed/update/*
  // 11. Sales Navigator: linkedin.com/sales/*
  // 12. Recruiter: linkedin.com/talent/*
  // Also support country-specific domains like linkedin.com/in-en/, linkedin.com/es/, etc.

  const linkedinUrlRegex =
    /^(?:https?:\/\/)?(?:www\.)?linkedin\.com\/(?:in|company|school|showcase|posts|pulse|jobs|groups|events|learning|feed|sales|talent|[a-z]{2}(?:-[a-z]{2})?)\/.+$/i;

  // Additional validation checks
  const checks = {
    // Must contain linkedin.com domain
    hasLinkedinDomain: /linkedin\.com/i.test(trimmedValue),
    
    // Should not have invalid characters in the path
    hasValidPath: !/[<>{}|\\^`\[\]]/.test(trimmedValue),
    
    // Check if it's a valid LinkedIn path type
    hasValidPathType: /linkedin\.com\/(?:in|company|school|showcase|posts|pulse|jobs|groups|events|learning|feed|sales|talent|[a-z]{2}(?:-[a-z]{2})?)\//i.test(trimmedValue)
  };

  // Validate the URL format
  if (!linkedinUrlRegex.test(trimmedValue)) {
    return 'Please enter a valid LinkedIn URL';
  }

  // Additional checks
  if (!checks.hasLinkedinDomain) {
    return 'URL must be from linkedin.com';
  }

  if (!checks.hasValidPath) {
    return 'LinkedIn URL contains invalid characters';
  }

  if (!checks.hasValidPathType) {
    return 'Please enter a valid LinkedIn profile, company, or page URL';
  }

  // Optional: Validate that personal profiles don't end with just /in/ or /in
  if (/linkedin\.com\/in\/?$/i.test(trimmedValue)) {
    return 'Please enter a complete LinkedIn profile URL';
  }

  // Optional: Validate that company pages don't end with just /company/ or /company
  if (/linkedin\.com\/company\/?$/i.test(trimmedValue)) {
    return 'Please enter a complete LinkedIn company page URL';
  }

  return VALID;
};